import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import _ from 'lodash';

import { ReactComponent as TopoNodeIconLocal } from 'assets/node-local-icon.svg';
import { ReactComponent as TopoNodeIconRemote } from 'assets/node-remote-icon.svg';
import { ReactComponent as DeviceIconLocal } from 'assets/device-local-icon.svg';
import { ReactComponent as DeviceIconRemote } from 'assets/device-remote-icon.svg';
import { ReactComponent as CollapsedTopoNodeIcon } from 'assets/plus-circle-solid.svg';
import { ReactComponent as UncollapsedTopoNodeIcon } from 'assets/minus-circle-regular.svg';
import { ReactComponent as NoChildrenTopoNodeIcon } from 'assets/circle-regular.svg';
import { LOCAL_RESOURCE_MAPPED_VALUES } from 'utils/appConstants';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const maxLabelLength = 40; // truncate label characters

const Node = styled.g`
  cursor: pointer;
`;

const TopoNodeIconContainer = styled.g`
  height: 30px;
  width: 30px;
  position: relative;
  top: 0;
  left: 0;

  * {
    cursor: ${({ hasChildren }) => (hasChildren ? 'pointer' : 'default')};
    fill: url(#nodeGradient);
  }
`;

const TopoNodeIconBackground = styled.div`
  background-color: white;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 40%;
  width: 100%;
  height: 100%;
`;

const NodeText = styled.text`
  font-family: Crossten;
  font-weight: ${({ isRemote }) => (isRemote ? 'normal' : '500')};
  font-size: 14px;

  :hover {
    text-decoration: underline;
  }
`;

const TreeNode = ({ nodeData, onLabelClick, clickHandler, isSelected }) => {
  const labelRef = React.createRef();
  const textRef = React.createRef();
  const topoNodeIcon = React.createRef();

  const [textWidth, setTextWidth] = useState(0);

  useEffect(() => {
    if (labelRef && labelRef.current) {
      // handle hover state
      d3.select(labelRef.current)
        .on('mouseenter', function (label) {
          d3.select(label).select('text').text(_.get(nodeData, 'text.name'));
          setSelectedTextWidth();
        })
        .on('mouseleave', function (label) {
          truncateTextLength();
        });
    }

    if (textRef && textRef.current) {
      setSelectedTextWidth();
    }
  }, []);

  const getSelectedTextWidth = () => {
    return textRef?.current?.getComputedTextLength();
  };

  const setSelectedTextWidth = () => {
    const selectedTextWidth = getSelectedTextWidth();
    setTextWidth(selectedTextWidth);
  };

  const renderNodeIcon = nodeData => {
    const iconX = '-18';
    const iconY = '-2.1em';
    const { data } = nodeData.node.data;
    //if the resource is a device
    if (data.deviceId) {
      if (
        externallyManagedEdevs &&
        data.localDevice !== LOCAL_RESOURCE_MAPPED_VALUES.LOCAL
      ) {
        return <DeviceIconRemote x={iconX} y={iconY} />;
      }
      return <DeviceIconLocal x={iconX} y={iconY} />;
    }
    //if the resource is a toponode
    if (
      externallyManagedEdevs &&
      data.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.LOCAL
    ) {
      return <TopoNodeIconRemote x={iconX} y={iconY} />;
    }
    return <TopoNodeIconLocal x={iconX} y={iconY} />;
  };

  const truncateTextLength = () => {
    const label = _.get(nodeData, 'text.name');
    const labelIsLongerThanMax = label.length > maxLabelLength;
    d3.select(labelRef.current)
      .select('text')
      .text(
        `${label.slice(0, maxLabelLength - 3)}${
          labelIsLongerThanMax ? '...' : ''
        }`
      );
    setSelectedTextWidth();
  };

  const labelClick = nodeData => {
    const selectedTextWidth = getSelectedTextWidth();
    setTextWidth(selectedTextWidth);
    onLabelClick(nodeData);
  };

  const { data } = nodeData.node.data;
  const isRemote =
    (externallyManagedEdevs &&
      data.localNode === LOCAL_RESOURCE_MAPPED_VALUES.REMOTE) ||
    (externallyManagedEdevs &&
      data.localDevice === LOCAL_RESOURCE_MAPPED_VALUES.REMOTE);
  let label = nodeData.text.name;
  label =
    label.length > maxLabelLength
      ? `${label.slice(0, maxLabelLength - 3)}...`
      : label;

  return (
    <Node {...nodeData.node.attributes}>
      <TopoNodeIconContainer
        hasChildren={nodeData.hasChildren}
        role="button"
        ref={topoNodeIcon}
      >
        <foreignObject
          x="-4"
          y="-5.5"
          height="13"
          width="13"
          role="button"
          onClick={() => clickHandler(nodeData)}
        >
          <TopoNodeIconBackground />
        </foreignObject>
        {nodeData.hasChildren ? (
          nodeData.node.children ? (
            <UncollapsedTopoNodeIcon
              x="-4"
              y="-5.5"
              height="13"
              width="13"
              role="button"
              onClick={() => clickHandler(nodeData)}
            />
          ) : (
            <CollapsedTopoNodeIcon
              x="-4"
              y="-5.5"
              height="13"
              width="13"
              role="button"
              onClick={() => clickHandler(nodeData)}
            />
          )
        ) : (
          <NoChildrenTopoNodeIcon
            x="-4"
            y="-5.5"
            height="13"
            width="13"
            role="button"
          />
        )}
      </TopoNodeIconContainer>

      <g role="button" ref={labelRef} onClick={_ => labelClick(nodeData)}>
        <rect
          width={textWidth + 21}
          height="16"
          x="-7"
          y="-1.4em"
          rx="2"
          fill={isSelected ? 'url(#nodeGradient)' : 'white'}
          opacity="0.75"
        />
        {renderNodeIcon(nodeData)}
        <NodeText
          ref={textRef}
          {...nodeData.text.attributes}
          fill={isRemote ? '#3F3F3F' : 'black'}
          isRemote={isRemote}
        >
          {label}
        </NodeText>
      </g>
    </Node>
  );
};

export default TreeNode;
